import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { RadioCard } from '../components/RadioCard';
import type { AgentTemplateWizardStepType, WizardStepProps } from '../types';

interface SelectOptionItem {
  label: string;
  value: string;
  description?: string;
}

/**
 * Generic `select` template step (design §11.3): `options.items` rendered as
 * the radio-card list, or a compact Select when `options.compact === true`.
 * Advisory in P2 like the generic text step (§10.3, §25 Q3).
 */
function GenericSelectStep({ step, value, onChange, errors, onErrors }: WizardStepProps) {
  const options = (step.options ?? {}) as { items?: unknown; compact?: boolean };
  const items = (Array.isArray(options.items) ? options.items : []).filter(
    (item): item is SelectOptionItem =>
      Boolean(
        item &&
        typeof item === 'object' &&
        typeof (item as SelectOptionItem).value === 'string' &&
        typeof (item as SelectOptionItem).label === 'string',
      ),
  );
  const selected = typeof value === 'string' && value.length > 0 ? value : '';
  const invalid = Boolean(errors.value);

  const handleSelect = (next: string) => {
    onChange(next);
    onErrors({});
  };

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This step declares no options — nothing to pick here.
      </p>
    );
  }

  if (options.compact) {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`step-${step.id}`}>{step.title}</Label>
        <Select value={selected || undefined} onValueChange={handleSelect}>
          <SelectTrigger id={`step-${step.id}`} className="w-full max-w-sm">
            <SelectValue placeholder="Choose an option" />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {invalid ? <p className="text-sm text-destructive">{errors.value}</p> : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <RadioCard
          key={item.value}
          groupName={`select-step-${step.id}`}
          value={item.value}
          checked={selected === item.value}
          onSelect={handleSelect}
        >
          <span className="block text-sm font-medium">{item.label}</span>
          {item.description ? (
            <span className="block text-sm text-muted-foreground">{item.description}</span>
          ) : null}
        </RadioCard>
      ))}
      {invalid ? <p className="text-sm text-destructive">{errors.value}</p> : null}
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'select';
export const component = GenericSelectStep;
